// KONTRAK lintas-layar (pemilik: kelompok "driver"). v1: A.tracker di public/js/ops.js —
// pengirim GPS live (driver saat perjalanan, sales saat kunjungan DKS) + Wake Lock. Dipakai: tugas, DKS, logout.
// Pelacak GPS: aktif selama perjalanan; kirim tiap ≥10 dtk atau pindah ≥15 m. Layar dijaga menyala (Wake Lock).
import { useSyncExternalStore } from 'react';
import { ApiError, api } from '../../lib/api.js';
import { haversine, type LatLng } from '../../lib/geo.js';

type Listener = () => void;

export interface Tracker {
  /** Mulai kirim posisi tiap beberapa detik (POST /api/gps). */
  start: () => void;
  /** Berhenti kirim posisi. */
  stop: () => void;
  /** Sedang aktif? */
  readonly active: boolean;
  /** Teks status GPS (v1: tracker.status, tampil di #gpsStatus). */
  readonly status: string;
  /** Ubah teks status (mis. 'simulasi demo — …'). */
  setStatus: (s: string) => void;
  /** Waktu kirim terakhir (ms); 0 = kirim posisi berikutnya segera (v1: tracker.lastSent = 0). */
  lastSent: number;
  /** Jaga layar tetap menyala (Wake Lock). */
  keepAwake: () => Promise<void>;
  /** Langganan perubahan status. Mengembalikan fungsi berhenti langganan. */
  subscribe: (fn: Listener) => () => void;
  /**
   * Dipanggil saat server menolak posisi karena sesi perjalanan/kunjungan sudah berakhir (409) — v1 lalu A.render().
   * Layar aktif (tugas/DKS) mendaftar untuk memuat ulang dirinya. Mengembalikan fungsi berhenti langganan.
   */
  onEnded: (fn: Listener) => () => void;
}

let watch: number | null = null;
let last: LatLng | null = null;
let wake: WakeLockSentinel | null = null;
let status = 'mati';
const listeners = new Set<Listener>();
const ended = new Set<Listener>();

async function onPos(p: GeolocationPosition) {
  const c = p.coords;
  const now = Date.now();
  const dist = last ? haversine(last, c) : Number.POSITIVE_INFINITY;
  if (now - tracker.lastSent < 10000 && dist < 15) return;
  if (now - tracker.lastSent < 3000) return;
  tracker.lastSent = now;
  last = { latitude: c.latitude, longitude: c.longitude };
  try {
    await api('POST', '/api/gps', { lat: c.latitude, lng: c.longitude, acc: c.accuracy, speed: c.speed, heading: c.heading });
    tracker.setStatus(
      `Terkirim ${new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })} · akurasi ±${Math.round(c.accuracy)} m`,
    );
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      tracker.stop();
      for (const fn of ended) fn();
    } else tracker.setStatus(`Gagal kirim lokasi (${(e as Error).message}) — akan dicoba lagi`);
  }
}

export const tracker: Tracker = {
  start() {
    if (watch != null) return;
    if (!navigator.geolocation) {
      this.setStatus('GPS tidak didukung HP ini');
      return;
    }
    watch = navigator.geolocation.watchPosition(
      (p) => void onPos(p),
      (e) => this.setStatus(e.code === 1 ? 'Izin lokasi DITOLAK — aktifkan di pengaturan browser' : 'Mencari sinyal GPS…'),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 },
    );
    void this.keepAwake();
    this.setStatus('Mencari sinyal GPS…');
  },
  stop() {
    if (watch != null) navigator.geolocation.clearWatch(watch);
    watch = null;
    if (wake) {
      wake.release().catch(() => {});
      wake = null;
    }
    this.setStatus('mati');
  },
  get active() {
    return watch != null;
  },
  get status() {
    return status;
  },
  setStatus(s) {
    status = s;
    for (const fn of listeners) fn();
  },
  lastSent: 0,
  async keepAwake() {
    try {
      if ('wakeLock' in navigator && !wake) {
        wake = await navigator.wakeLock.request('screen');
        wake.addEventListener('release', () => {
          wake = null;
        });
      }
    } catch {
      /* tidak didukung */
    }
  },
  subscribe(fn) {
    listeners.add(fn);
    return () => void listeners.delete(fn);
  },
  onEnded(fn) {
    ended.add(fn);
    return () => void ended.delete(fn);
  },
};

if (typeof document !== 'undefined')
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && watch != null) void tracker.keepAwake();
  });

/** Teks status GPS terkini (berubah otomatis) — untuk `GPS: <span id="gpsStatus">…</span>`. */
export function useTrackerStatus(): string {
  return useSyncExternalStore(tracker.subscribe, () => tracker.status);
}
